import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';

import SubscriptionTermsDisclosure from './SubscriptionTermsDisclosure';
import { useMembershipPlanPricing } from '../hooks/useMembershipPlanPricing';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import { getProduct } from '../subscriptions/catalog';
import {
  canShowFreeTrialChoice,
  isMembershipTrialing,
  MEMBERSHIP_PURCHASE_CHOICE_EXPLAINER,
} from '../subscriptions/membershipPurchaseUi';
import type { MembershipPlanId, MembershipState } from '../subscriptions/types';
import { isIosNativeApp } from '../utils/nativeCheckoutBridge';

export interface SubscriptionPurchaseCtaProps {
  assignmentId: number;
  productId: MembershipPlanId;
  returnTo?: string;
  compact?: boolean;
  membership?: MembershipState | null;
}

/** Inline "subscribe to keep going" prompt used inside the program steppers. */
const SubscriptionPurchaseCta: React.FC<SubscriptionPurchaseCtaProps> = ({
  assignmentId,
  productId,
  returnTo,
  compact = false,
  membership = null,
}) => {
  const product = getProduct(productId);
  const pricing = useMembershipPlanPricing()[productId];
  const { loading, error, clearError, startCheckout, openBillingPortal } = useSubscriptionCheckout();
  const trialing = isMembershipTrialing(membership);
  const trialChoice = canShowFreeTrialChoice(membership, pricing.trialLabel);
  const native = isIosNativeApp();

  if (!product) {
    return null;
  }

  const runCheckout = async (skipTrial: boolean) => {
    clearError();
    await startCheckout({ assignmentId, productId, returnTo, skipTrial });
  };

  const runUpgradeFromTrial = async () => {
    clearError();
    if (native || membership?.provider === 'apple_iap') {
      return;
    }
    const url = await openBillingPortal(assignmentId);
    if (url) {
      window.location.assign(url);
    }
  };

  const handleRetry = () => {
    if (trialing) {
      void runUpgradeFromTrial();
    } else {
      void runCheckout(false);
    }
  };

  return (
    <Box
      sx={{
        p: compact ? 1.5 : 2,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="subscription-purchase-cta"
    >
      <Typography variant={compact ? 'subtitle2' : 'subtitle1'} gutterBottom>
        {trialing ? 'Unlock the full journey now' : 'Unlock the full journey'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {product.name} · {pricing.price}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            error.retryable ? (
              <Button color="inherit" size="small" onClick={handleRetry} disabled={loading}>
                Try again
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2">{error.message}</Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {error.instruction}
          </Typography>
        </Alert>
      )}

      {trialing ? (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are on a free trial. Subscribe now to unlock all sessions and growth reports without
            waiting for the trial to end.
          </Typography>
          {native || membership?.provider === 'apple_iap' ? (
            <Typography variant="body2" color="text.secondary">
              Billed by Apple — manage this membership in your Apple Account settings to change
              billing or end the trial early.
            </Typography>
          ) : (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => void runUpgradeFromTrial()}
                disabled={loading}
                data-attr="subscription-purchase_subscribe-now"
              >
                {loading ? 'Opening…' : 'Subscribe now'}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Opens billing management to start paid membership immediately.
              </Typography>
            </>
          )}
        </>
      ) : trialChoice ? (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => void runCheckout(false)}
              disabled={loading}
              data-attr="subscription-purchase_start-trial"
            >
              {loading ? 'Starting checkout…' : 'Start free trial'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => void runCheckout(true)}
              disabled={loading}
              data-attr="subscription-purchase_subscribe-now"
            >
              {loading ? 'Starting checkout…' : 'Subscribe now'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {MEMBERSHIP_PURCHASE_CHOICE_EXPLAINER}
          </Typography>
          <SubscriptionTermsDisclosure
            trialLabel={pricing.trialLabel}
            priceLabel={pricing.price}
            compact
          />
        </>
      ) : (
        <>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => void runCheckout(true)}
            disabled={loading}
            data-attr="subscription-purchase_buy"
            sx={{ mb: 1.5 }}
          >
            {loading ? 'Starting checkout…' : 'Subscribe'}
          </Button>
          <SubscriptionTermsDisclosure priceLabel={pricing.price} compact />
        </>
      )}
    </Box>
  );
};

export default SubscriptionPurchaseCta;
